import { useCustomToast } from '@nexelem/snackbar';
import { PlanView, useProductionPlanQueryParams } from '../../hooks';
import { useCustomTranslation } from '../../../../../hooks';
import { debounce } from 'lodash';
import { isElementInViewport, retry, scrollIntoViewAsync } from '@nexelem/utils';
import { queueByIdSelector, useGroupedQueuesStore, useProductionPlanSearchStore } from '../../store';

type CenterGanttProps = {
  queueId: number;
  taskId: number | string;
  taskStartDate?: Date;
  markAsSearchSelected?: boolean;
};

export const useProductionTaskLocate = () => {
  const { queryParams } = useProductionPlanQueryParams();
  const displayToast = useCustomToast();
  const { tWrapper } = useCustomTranslation('products.productionPlan.gantt');
  const getQueueById = useGroupedQueuesStore((state) => (id: number) => queueByIdSelector(id)(state));
  const setSearchSelectedTaskId = useProductionPlanSearchStore((state) => state.setSearchSelectedTaskId);
  const isKanban = queryParams.view === PlanView.KANBAN;

  const displayErrorToast = debounce(() => {
    displayToast({
      message: tWrapper('cannotLocate'),
      toastType: 'error',
    });
  }, 1000);

  return async ({ taskId, queueId, markAsSearchSelected }: CenterGanttProps) => {
    const queue = getQueueById(queueId);

    if (queue?.isHidden) {
      displayToast({
        message: tWrapper('cannotLocateQueue'),
        toastType: 'error',
      });

      return;
    }

    const ganttScrollContainer = document.getElementById(scrollContainerId);

    if (!ganttScrollContainer) {
      return;
    }

    const rowSelector = getQueueSelector(queueId);
    const ganttQueueRow = await retry(() => document.querySelector(rowSelector));

    if (ganttQueueRow) {
      if (isKanban) {
        const MAX_ATTEMPTS = 5;
        let attempts = 0;

        do {
          await scrollIntoViewAsync(ganttScrollContainer, ganttQueueRow, {
            block: 'start',
            behavior: 'smooth',
            inline: 'start',
          }).catch();
          if (isElementInViewport(ganttQueueRow)) {
            break;
          }
          attempts++;
        } while (attempts < MAX_ATTEMPTS);
      } else {
        await scrollIntoViewAsync(ganttScrollContainer, ganttQueueRow, { block: 'center', behavior: 'smooth' }).catch();
      }
      const taskSelector = getNodeSelector(taskId);
      const node = await retry(() => ganttQueueRow.querySelector(taskSelector));

      if (!node) {
        displayErrorToast();
        return;
      }

      await scrollIntoViewAsync(ganttScrollContainer, node, {
        block: 'center',
        inline: 'center',
        behavior: 'smooth',
      }).catch();

      if (markAsSearchSelected) {
        setSearchSelectedTaskId(taskId);
      }
    } else {
      displayErrorToast();
    }
  };
};

const addQueueSelector = (queueId: number) => {
  return {
    'data-queue-id': queueId,
  };
};
const getQueueSelector = (queueId: number) => `[data-queue-id="${queueId}"]`;

const addNodeSelector = (id: string | number) => {
  return { 'data-task-id': id };
};
const getNodeSelector = (id: string | number) => `[data-task-id="${id}"]`;

const scrollContainerId = 'gantt-scroll-container';

export const LocateProductionTask = {
  addQueueSelector,
  getQueueSelector,
  addNodeSelector,
  getNodeSelector,
  scrollContainerId,
};
